import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import { Navbar } from "@/components/Navbar";
import { KeyRound, Copy, Check, RefreshCw, ShieldX, Users } from "lucide-react";

function CopyField({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-black/10 bg-ek-canvas px-3 py-2">
      <code data-testid={testid} className="flex-1 overflow-x-auto whitespace-nowrap font-mono text-xs text-ek-ink">{value}</code>
      <button type="button" onClick={copy} className="shrink-0 text-ek-ink/65 hover:text-ek-purple" title="Copy">
        {copied ? <Check size={15} className="text-green-600" /> : <Copy size={15} />}
      </button>
    </div>
  );
}

export default function Provisioning() {
  const [config, setConfig] = useState(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const scimBase = `${API}/scim/v2`;

  const load = () => api.get("/scim/config").then(({ data }) => setConfig(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/scim/token");
      setToken(data.token);
      await load();
      toast.success("SCIM token generated. Copy it now — it won't be shown again.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not generate token.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete("/scim/token");
      setToken("");
      await load();
      toast.success("SCIM token revoked.");
    } catch {
      toast.error("Could not revoke token.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 py-8" data-testid="provisioning-page">
        <Seo title="Directory provisioning (SCIM)" description="Automated user provisioning via SCIM 2.0 for Okta and Microsoft Entra." path="/provisioning" />
        <p className="ek-eyebrow">Owner · Enterprise</p>
        <h1 className="ek-heading mt-1 text-3xl">Directory provisioning (SCIM 2.0)</h1>

        <IntroNote>
          Connect your identity provider (Okta, Microsoft Entra) to automatically provision and
          deprovision users. Point your IdP at the SCIM Base URL below and authenticate with the
          bearer token you generate here. Deprovisioning disables the account (it is not hard-deleted).
        </IntroNote>

        {/* Endpoint */}
        <div className="rounded-xl border border-black/5 bg-white p-6" data-testid="scim-endpoint-card">
          <p className="text-sm font-semibold text-ek-ink">SCIM Base URL</p>
          <p className="mb-2 mt-0.5 text-xs text-ek-ink/65">Paste this into your IdP's SCIM connector configuration.</p>
          <CopyField value={scimBase} testid="scim-base-url" />
          {config && (
            <div className="mt-4 flex flex-wrap gap-6 text-sm">
              <span className="inline-flex items-center gap-1.5 text-ek-ink/60">
                <span className={`h-2 w-2 rounded-full ${config.configured ? "bg-green-500" : "bg-slate-300"}`} />
                <span data-testid="scim-status">{config.configured ? "Token active" : "No token configured"}</span>
              </span>
              <span className="inline-flex items-center gap-1.5 text-ek-ink/60">
                <Users size={14} /> <span data-testid="scim-provisioned-count">{config.provisioned_count}</span> provisioned users
              </span>
              {config.last_used_at && (
                <span className="text-ek-ink/65">Last sync: {new Date(config.last_used_at).toLocaleString()}</span>
              )}
            </div>
          )}
        </div>

        {/* Token */}
        <div className="mt-6 rounded-xl border border-black/5 bg-white p-6" data-testid="scim-token-card">
          <p className="text-sm font-semibold text-ek-ink">Bearer token</p>
          <p className="mt-0.5 text-xs text-ek-ink/65">Generating a new token immediately invalidates any previous one.</p>

          {token && (
            <div className="mt-3" data-testid="scim-token-reveal">
              <p className="mb-1 text-xs font-medium text-amber-700">Copy this now — it will not be shown again.</p>
              <CopyField value={token} testid="scim-token-value" />
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" onClick={generate} disabled={busy} data-testid="scim-generate-button" className="ek-btn disabled:opacity-60">
              {config?.configured ? <RefreshCw size={15} /> : <KeyRound size={15} />}
              {config?.configured ? "Rotate token" : "Generate token"}
            </button>
            {config?.configured && (
              <button type="button" onClick={revoke} disabled={busy} data-testid="scim-revoke-button" className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                <ShieldX size={15} /> Revoke
              </button>
            )}
          </div>
        </div>

        {/* Quick guide */}
        <div className="mt-6 rounded-xl border border-black/5 bg-white p-6">
          <p className="text-sm font-semibold text-ek-ink">Connecting your IdP</p>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-ek-ink/60">
            <li>In Okta or Entra, add a SCIM 2.0 provisioning app.</li>
            <li>Set the SCIM connector base URL to the value above.</li>
            <li>Choose <b>HTTP Header / Bearer token</b> auth and paste the token you generated.</li>
            <li>Map <code className="font-mono text-xs">userName</code> → email, plus given/family name. Enable Create, Update and Deactivate.</li>
            <li>Provisioned users sign in via your IdP (SSO); they appear under Users &amp; access.</li>
          </ol>
        </div>
      </main>
    </div>
  );
}
